import type { ReactNode } from 'react';

import type { JobSummary } from '../api/types';
import {
  FAILURE_CATEGORY_R_IS_REAL_FAILURE,
  FAILURE_CATEGORY_R_LABEL,
  FAILURE_CATEGORY_R_SUBLABEL,
} from '../api/types';
import { renderError } from '../lib/error_display';
import { ageHuman, drColor, fmtResidual } from '../lib/format';
import { residualNotice } from '../lib/residualDisplay';
import { GnssStack } from './GnssStack';
import { StatusPill } from './StatusPill';
import './JobCard.css';

/** One row in the Feed list — single source of truth for the visual
 *  contract between API state and UI.
 *
 *  Layout (matches prototype lines 6446-6482):
 *
 *  ::
 *
 *      ┌─[STATE-COLOR]
 *      │ STATION · GnssStack · DATE                       #ID
 *      │ ● ожидание · tag                  «right-aligned meta»
 *      │ ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░  (progress bar — submitted only)
 *      └─
 */
export function JobCard({ job, onClick }: { job: JobSummary; onClick?: () => void }) {
  return (
    <div className={`job-card ${job.state}`} onClick={onClick} role="button" tabIndex={0}>
      <div className="row1">
        <span className="station">{job.station_code ?? '—'}</span>
        <span className="meta-sep">·</span>
        <GnssStack systems={job.gnss_systems} />
        <span className="meta-sep">·</span>
        <span className="date">{job.obs_date ?? '—'}</span>
        <span className="id">#{job.id}</span>
      </div>
      <div className="row2">
        <div className="left">
          <StatusPill state={job.state} failureCategory={job.failure_category} />
          {job.tag && <span className="tag">· {job.tag}</span>}
          {/* Phase 3.15 — small badge for jobs the scheduler kept on PPP
              after a compute_failed result (e.g. WHIT × BDS-only). Sits
              next to the tag because operators scan that area for batch
              context anyway. */}
          {job.preserve_on_ppp && (
            <span
              className="diagnostic-badge"
              title="Сохранена на PPP для диагностики"
            >
              🔬 диагностика
            </span>
          )}
        </div>
        <div className="right">{renderRightMeta(job)}</div>
      </div>
      {job.state === 'submitted' && (
        <div className="progress">
          <div className="fill" />
        </div>
      )}
    </div>
  );
}

function renderRightMeta(job: JobSummary): ReactNode {
  switch (job.state) {
    case 'pending':
      return <span className="meta-pending">в очереди</span>;
    case 'submitted':
      return <span className="meta-info">{ageHuman(job.submitted_at)} на сервисе</span>;
    case 'done':
      // Phase 3.13.2 — catalogue-collision rows (P117 / P101) have
      // ``dr_3d_mm`` in millions of millimetres; the numeric pill
      // would read like "8485740177.4 мм" — useless. Replace with a
      // single "⚠ аномалия" tag; users open DetailScreen to read the
      // multi-line explanation.
      if (job.has_quality_warning) {
        return (
          <span
            className="dr-anomaly"
            title="Аномалия |dR|: коллизия 4-character station-кода в IGS-каталоге"
          >
            ⚠ аномалия
          </span>
        );
      }
      // Wave 4b — a DONE job with no residual because no IGS reference
      // station existed in the ±5-day window is not a gap in OUR
      // pipeline; say so plainly («○ нет эталона») instead of a bare
      // dash, so the operator doesn't read it as a missing number.
      // Other skip reasons (compute_failed_no_coords) keep the dash —
      // those ARE our side and the DetailScreen explains them.
      if (job.residual_3d_mm == null) {
        // P1 · 2B — branch on the server-computed residual_display class
        // (never re-derive the rules). operational_failure (401/timeout) gets a
        // VISIBLE technical glyph — not «нет эталона», not a bare dash. When the
        // class is absent (an old/cached response) fall back to legacy skip_reason.
        const display = job.residual_display;
        if (display) {
          const notice = residualNotice(display, job.residual_failure_code);
          if (display === 'absent') {
            return (
              <span className="meta-info" title={notice.text ?? undefined}>
                ○ нет эталона
              </span>
            );
          }
          if (display === 'operational_failure') {
            return (
              <span className="dr-anomaly" title={notice.text ?? undefined}>
                ⚡ техн. ошибка
              </span>
            );
          }
          if (display === 'unknown') {
            return (
              <span className="dr-anomaly" title={notice.text ?? undefined}>
                ⚡ ?
              </span>
            );
          }
          return <span>—</span>;
        }
        if (job.residual_skip_reason === 'no_igs_reference_within_window') {
          return (
            <span
              className="meta-info"
              title="Станции нет в IGS Daily Combined в окне ±5 дней — сравнивать не с чем"
            >
              ○ нет эталона
            </span>
          );
        }
        return <span>—</span>;
      }
      return (
        <>
          <span style={{ color: drColor(job.residual_3d_mm) }}>
            {fmtResidual(job.residual_3d_mm)}
          </span>
          <span className="unit">мм · ITRF20</span>
        </>
      );
    case 'failed': {
      // Phase 3.2.1: ``className="error"`` is required for the row2 flex
      // truncation rule in JobCard.css — without an explicit class the
      // long ``login failed: …`` string would push past the .right column
      // and overlap the .left tag.
      //
      // Phase 4.15 → 4.12: route the raw ``error_summary`` through
      // ``renderError`` so well-known categories render fixed Russian
      // phrases instead of the mid-word-truncated backend
      // ``_strip_tech_prefix(...)[:80]`` slice. Unknown errors fall
      // through to the renderer's default branch (prefix stripped,
      // 80-char clip with ellipsis). Phase 4.12 — same logic as the
      // previous ``makeErrorPreview`` shim; we now read the ``.shortText``
      // off the centralised renderer so this surface picks up new
      // categories automatically.
      //
      // Phase R: re-route the colour based on the operator-facing
      // bucket. Only `service_failed` keeps the red ``error`` class;
      // everything else (catalogue-reality / infrastructure) renders
      // with a muted ``muted-failure`` class + the R-taxonomy label
      // so the operator stops reading «785 ОШИБОК» as a system
      // health signal.
      const cat = job.failure_category;
      const isRealFailure =
        cat == null || FAILURE_CATEGORY_R_IS_REAL_FAILURE[cat] === true;
      if (!isRealFailure) {
        return (
          <span
            className="muted-failure"
            data-testid="job-card-failure-muted"
          >
            {FAILURE_CATEGORY_R_LABEL[cat]} · {FAILURE_CATEGORY_R_SUBLABEL[cat]}
          </span>
        );
      }
      const rendered = renderError(job.error_summary);
      return (
        <span className="error" data-testid="job-card-failure-accent">
          {cat === 'service_failed'
            ? `не вычислено · ${rendered?.shortText || 'сбой сервиса'}`
            : rendered?.shortText || 'неизвестная ошибка'}
        </span>
      );
    }
    case 'cancelled':
      return <span className="meta-cancelled">отменена</span>;
  }
}

/** Pulsing skeleton placeholder. Rendered N-times during the first
 *  ``useJobs`` fetch so the list doesn't appear empty mid-load. */
export function JobCardSkeleton() {
  return <div className="job-card-skeleton" aria-hidden />;
}
